import { useState, useEffect } from 'react';
import ModalBase from '@/components/base/ModalBase';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { PaymentProcessor } from '@/mocks/superAdminPaymentProcessors';

interface Props {
  open: boolean;
  onClose: () => void;
  processor: PaymentProcessor | null;
  onSave: (processorId: string, percent: number, fixed: number) => void;
}

export default function ProcessorCommissionModal({ open, onClose, processor, onSave }: Props) {
  const [percent, setPercent] = useState(0);
  const [fixed, setFixed] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !processor) return;
    setPercent(processor.commissionPercent);
    setFixed(processor.commissionFixed);
  }, [open, processor]);

  if (!processor) return null;

  const handleSave = async () => {
    if (percent < 0 || fixed < 0) {
      SwalAlert.warning('Valores inválidos', 'El porcentaje y el monto fijo no pueden ser negativos.');
      return;
    }
    setSaving(true);
    await new Promise((r) => setTimeout(r, 500));
    setSaving(false);
    onSave(processor.id, percent, fixed);
    SwalAlert.toast('Comisión actualizada', 'success');
    onClose();
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={`Comisión — ${processor.name}`}
      subtitle="Configurá el porcentaje y monto fijo por transacción"
      size="sm"
      headerIcon={
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${processor.logoColor}`}>
          <i className={`${processor.icon} text-sm`}></i>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-foreground-700 text-[10px] font-semibold mb-1">
            Porcentaje (%)
          </label>
          <div className="relative">
            <input
              type="number"
              value={percent}
              onChange={(e) => setPercent(parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 pr-10 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">%</span>
          </div>
          <p className="text-foreground-400 text-[9px] mt-1">Porcentaje sobre el monto de cada transacción</p>
        </div>

        <div>
          <label className="block text-foreground-700 text-[10px] font-semibold mb-1">
            Monto fijo (USD)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">$</span>
            <input
              type="number"
              value={fixed}
              onChange={(e) => setFixed(parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 pl-7 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <p className="text-foreground-400 text-[9px] mt-1">Monto fijo que se suma a cada transacción</p>
        </div>

        {/* Preview */}
        <div className="bg-background-100 rounded-xl p-3">
          <p className="text-foreground-400 text-[9px] font-medium mb-1">Vista previa</p>
          <p className="text-foreground-800 text-xs">
            Para un cobro de <strong>$100.00</strong>, la comisión será{' '}
            <strong className="text-foreground-950">
              ${(100 * percent / 100 + fixed).toFixed(2)}
            </strong>
            {percent > 0 && fixed > 0 && (
              <span className="text-foreground-400"> ({percent}% + ${fixed.toFixed(2)})</span>
            )}
            {percent > 0 && fixed === 0 && (
              <span className="text-foreground-400"> ({percent}%)</span>
            )}
            {percent === 0 && fixed > 0 && (
              <span className="text-foreground-400"> (fijo ${fixed.toFixed(2)})</span>
            )}
          </p>
        </div>
      </div>

      <ModalBase
        open={false}
        onClose={() => {}}
        title=""
        footer={
          <>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {saving ? (
                <span className="flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin text-xs"></i>Guardando...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <i className="ri-save-line text-xs"></i>Guardar
                </span>
              )}
            </button>
          </>
        }
      />
    </ModalBase>
  );
}